import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import type { UpdateUserRequest, UpdateUserResponse, User } from "../types/user-types";

export const getCurrentUser = async (): Promise<User> => {
  const url = getApiUrl(endpoint.user.getProfile);
  try {
    const responseData = await handleApiCall<{ user: User }>(async () => {
      const response = await api.get({
        url,
        auth: true,
      });
      return response;
    });

    const user = responseData?.user;

    if (!user || !user.id) {
      throw new Error('Invalid user data received from server');
    }

    return user;
  } catch (error) {

    throw error;
  }
};

/**
 * Whether this agent has linked Licensedproducer (signed in with InsuraCentral over
 * there, provisioning the lead-sync key). Powers the Licensedproducer teaser in
 * Settings → Profile. Never throws — degrades to not-linked on any error.
 */
export const getAgentVerifiedStatus = async (): Promise<{ linked: boolean; since: string | null }> => {
  try {
    const url = getApiUrl('/users/agentverified-status');
    const data = await handleApiCall<{ linked: boolean; since: string | null }>(async () => {
      const response = await api.get({ url, auth: true });
      return response;
    });
    return { linked: !!data?.linked, since: data?.since ?? null };
  } catch {
    return { linked: false, since: null };
  }
};

export const updateUser = async (
  userId: string,
  data: UpdateUserRequest,
  opts?: { silent?: boolean }
): Promise<User> => {
  // Use PUT /users/profile (no ID) which accepts UpdateOwnProfileValidator
  // This validator supports: name, email, title, contactPhone, defaultOutboundPhone, callForwardingEnabled
  const url = getApiUrl('/users/profile');
  try {
    // Build request data for UpdateOwnProfileValidator
    const dataRecord = data as Record<string, unknown>;
    const normalizedContactPhone = (dataRecord.contactPhone as string | undefined) ?? (dataRecord.phone as string | undefined);

    // Build request with all supported fields
    const requestData: Record<string, unknown> = {};

    if (dataRecord.name !== undefined) requestData.name = dataRecord.name;
    if (dataRecord.email !== undefined) requestData.email = dataRecord.email;
    if (dataRecord.title !== undefined) requestData.title = dataRecord.title;
    if (normalizedContactPhone) requestData.contactPhone = normalizedContactPhone;
    if (dataRecord.defaultOutboundPhone !== undefined) requestData.defaultOutboundPhone = dataRecord.defaultOutboundPhone;
    if (dataRecord.callForwardingEnabled !== undefined) requestData.callForwardingEnabled = dataRecord.callForwardingEnabled;
    if (dataRecord.smsForwardingEnabled !== undefined) requestData.smsForwardingEnabled = dataRecord.smsForwardingEnabled;
    const responseData = await handleApiCall<{ user: User }>(async () => {
      const response = await api.put({
        url,
        body: requestData,
        auth: true, // Ensure authentication is included
      });
      return response;
    });
    // Extract user from the nested response structure
    const updatedUser = responseData?.user;

    if (!opts?.silent) showToast("User updated successfully", "success");

    if (!updatedUser || !updatedUser.id) {
      throw new Error('Invalid user data received from server');
    }

    return updatedUser;
  } catch (error) {

    if (!opts?.silent) showToast(transformErrorResponse(error)?.message || "Failed to update user", "error");
    throw error;
  }
};
